import type {
  TReferralApointment,
  TReferralAppointmentActions,
} from "@/api/models/referral/appointment";
import ReferralAppointmentService from "@/api/models/referral/appointment";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import type {
  TComposableParams,
  TItemQueryComposableParams,
} from "@/composables/shared/filters/useGeneralFilter";
import {
  splitComposableParams,
  resolveSelectEnabled,
} from "@/composables/shared/filters/useGeneralFilter";
import {
  useGenericListQuery,
  useGenericPaginatedQuery,
  useGenericQuery,
  createQueryKeys,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import type { TSelectFieldDynamicProps } from "@/types/selectField";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";
import type { MaybeRefOrGetter } from "vue";

const ReferralAppointmentQueryKeys = createQueryKeys(
  ReferralAppointmentService.queryKey,
  (key) => ({
    detail: key<string>("detail"),
    list: key<DefaultPaginatedFilters>("list"),
    paginated: key<DefaultPaginatedFilters>("paginated"),
  })
);

export const useReferralAppointment = (
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery<TReferralApointment, string>({
    queryKey: ReferralAppointmentQueryKeys.detail(),
    params,
    fetcher: (id) => ReferralAppointmentService.get({ id }),
    permission: $permSubjects.REFERRAL__REFERRAL,
    ...options,
  });
};

export const useReferralAppointments = (
  filters?: MaybeRefOrGetter<Partial<DefaultPaginatedFilters>>,
  enabled: MaybeRefOrGetter<boolean> = true
) => {
  return useGenericListQuery({
    queryKey: ReferralAppointmentQueryKeys.list(),
    params: { filters, enabled },
    fetcher: (filters) => ReferralAppointmentService.getAll({}, filters),
    permission: $permSubjects.REFERRAL__REFERRAL,
  });
};

export const useReferralAppointmentsPaginated = (
  params: TOwnedFilterParams<DefaultPaginatedFilters> = {}
) => {
  return useGenericPaginatedQuery({
    params,
    queryKey: ReferralAppointmentQueryKeys.paginated(),
    fetcher: (fetchParams) =>
      ReferralAppointmentService.getPaginatedFiltered(
        {},
        fetchParams.filters,
        fetchParams.pageParam
      ),
    permission: $permSubjects.REFERRAL__REFERRAL,
  });
};

export const useCreateReferralAppointment = () => {
  return useGenericMutation<TReferralApointment, Partial<TReferralApointment>>({
    mutationFn: (data) => ReferralAppointmentService.create({}, data),
    feature: "referralAppointment.create",
  });
};

export const useUpdateReferralAppointment = () => {
  return useGenericMutation<TReferralApointment, TReferralApointment>({
    mutationFn: (data) => ReferralAppointmentService.update({ id: data.id! }, data),
    feature: "referralAppointment.update",
  });
};

export const useActionReferralAppointment = () => {
  return useGenericMutation<
    TReferralApointment,
    { id: string; action: TReferralAppointmentActions }
  >({
    mutationFn: ({ id, action }) => ReferralAppointmentService.action(id, action),
    feature: "referralAppointment.action",
  });
};

export const useReferralAppointmentForSelect = (
  extaParams?: TComposableParams & Partial<DefaultPaginatedFilters>
): TSelectFieldDynamicProps<TReferralApointment, DefaultPaginatedFilters> => {
  const { filters } = splitComposableParams<DefaultPaginatedFilters>(extaParams);
  const query = useReferralAppointmentsPaginated({
    initial: filters,
    enabled: () => resolveSelectEnabled(extaParams),
  });

  return {
    paginatedQuery: query,
    queryFilters: query.filters,
    filterField: "query",
    queryEnabled: query.queryEnabled,
  };
};
